// =====================================================================
// Faz V (2026-05-06) — Sales Pattern Heatmap (Saat/Gün)
// =====================================================================
// 7 gün × 24 saat = 168 hücrelik heatmap. Her hücrede o gün+saat'te
// gerçekleşmiş sipariş sayısı; renk koyulaştıkça yoğunluk artar.
//
// Owner kullanım örneği:
//   - "Salı 14:00'te peak — bu saatte mail gönder, push at"
//   - "Cumartesi sessiz — kampanyayı pazara kaydır"
//   - "Sabah 09:00 öncesi ölü — 09:30'dan sonra mailing başlat"
//
// Endpoint: GET /api/admin/insights/sales-pattern?days=90
//
// MySQL DAYOFWEEK: 1=Pazar...7=Cumartesi. TR'de Pazartesi başlangıç istiyoruz
// → MAP_DOW_TR array ile dönüştürüyoruz frontend'de.
// =====================================================================
import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Card, Typography, Grid, CircularProgress, Alert, IconButton,
  Tooltip, ToggleButton, ToggleButtonGroup
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import API from "../services/api";

// MySQL DOW (1=Pzr..7=Cmt) → TR sıralamada (0=Pzt..6=Pzr) dönüşüm
// MySQL: 1=Pzr 2=Pzt 3=Sal 4=Çar 5=Per 6=Cum 7=Cmt
// TR    : 6=Pzr 0=Pzt 1=Sal 2=Çar 3=Per 4=Cum 5=Cmt
const MYSQL_TO_TR_INDEX = { 1: 6, 2: 0, 3: 1, 4: 2, 5: 3, 6: 4, 7: 5 };
const TR_DAY_LABELS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Pzr"];
// Tam ad — peak metnine yazmak için
const TR_DAY_FULL = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];

const fmtNum = (v) => Number(v || 0).toLocaleString("tr-TR");
const fmtMoney = (v) => Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " ₺";

// Hücrenin yoğunluk rengi — sipariş sayısı / max'a göre 0-1 oran
// 0 → açık gri (#f1f5f9), 1 → koyu mavi (#0D47A1)
// Linear interpolation: HSL veya basit gradient
const cellColor = (count, max) => {
  if (max === 0 || count === 0) return "#f8fafc";
  const ratio = count / max;
  // Açık gri'den koyu maviye gradient (RGB lineer interpolasyon)
  // start: #e0e7ff (indigo 100), end: #1e3a8a (blue 900)
  const startR = 224, startG = 231, startB = 255;
  const endR = 30, endG = 58, endB = 138;
  const r = Math.round(startR + (endR - startR) * ratio);
  const g = Math.round(startG + (endG - startG) * ratio);
  const b = Math.round(startB + (endB - startB) * ratio);
  return `rgb(${r}, ${g}, ${b})`;
};

// Hücre rengine göre text rengi (kontrast için)
const cellTextColor = (count, max) => {
  if (max === 0 || count === 0) return "#94a3b8";
  return count / max > 0.5 ? "white" : "#1f2937";
};

export default function SalesPattern() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [days, setDays] = useState(90);

  const load = (d = days) => {
    setLoading(true);
    API.get(`/api/admin/insights/sales-pattern?days=${d}`)
      .then(r => { setData(r.data); setErr(null); })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(days); }, [days]);

  // Matrix → 7 (TR sırası: Pzt..Pzr) × 24 lookup map
  // useMemo ile cache — data değişmediği sürece tekrar hesaplanmaz
  const grid = useMemo(() => {
    // Boş 7×24 grid başlat
    const g = Array.from({ length: 7 }, () => Array(24).fill(0));
    if (!data?.matrix) return g;
    for (const m of data.matrix) {
      const trIdx = MYSQL_TO_TR_INDEX[Number(m.dow)];
      const hour = Number(m.hour);
      if (trIdx != null && hour >= 0 && hour < 24) {
        g[trIdx][hour] = Number(m.orders);
      }
    }
    return g;
  }, [data]);

  // Tüm hücrelerdeki max — renk ölçeklendirmesi için
  const maxCount = useMemo(() => {
    let max = 0;
    for (const row of grid) for (const v of row) if (v > max) max = v;
    return max;
  }, [grid]);

  if (loading && !data) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }
  if (err) return <Alert severity="error" sx={{ m: 2 }}>{err}</Alert>;
  if (!data) return null;

  // Peak day/hour metni için TR formatına dönüştür
  const peakDayTr = data.peak ? TR_DAY_FULL[MYSQL_TO_TR_INDEX[data.peak.dow]] : null;
  const peakHourLabel = data.peak ? `${String(data.peak.hour).padStart(2, "0")}:00` : null;

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Sales Pattern</Typography>
          <Typography variant="body2" color="text.secondary">
            Haftanın hangi gününün hangi saatinde sipariş geliyor?
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ToggleButtonGroup size="small" value={days} exclusive onChange={(_, v) => v && setDays(v)}>
            <ToggleButton value={30}>30g</ToggleButton>
            <ToggleButton value={90}>90g</ToggleButton>
            <ToggleButton value={180}>6 ay</ToggleButton>
            <ToggleButton value={365}>1 yıl</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Yenile">
            <IconButton onClick={() => load(days)}><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Genel KPI */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Toplam Sipariş</Typography>
            <Typography variant="h4" fontWeight={800}>{fmtNum(data.totals?.total_orders)}</Typography>
            <Typography variant="caption" color="text.disabled">son {days} gün</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Toplam Ciro</Typography>
            <Typography variant="h4" fontWeight={800} color="#1565C0">{fmtMoney(data.totals?.total_revenue)}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Aktif Gün</Typography>
            <Typography variant="h4" fontWeight={800} color="#10b981">{fmtNum(data.totals?.active_days)}</Typography>
            <Typography variant="caption" color="text.disabled">en az 1 sipariş gelen</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3, bgcolor: "#fff7ed", border: "2px solid #f97316" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
              <LocalFireDepartmentIcon sx={{ color: "#f97316", fontSize: 18 }} />
              <Typography variant="caption" color="text.secondary" fontWeight={700}>PEAK</Typography>
            </Box>
            {data.peak ? (
              <>
                <Typography variant="h6" fontWeight={800} color="#f97316">
                  {peakDayTr} {peakHourLabel}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  {data.peak.orders} sipariş · {fmtMoney(data.peak.revenue)}
                </Typography>
              </>
            ) : (
              <Typography color="text.disabled">Veri yok</Typography>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* Heatmap — ana görsel */}
      <Card sx={{ p: 2.5, borderRadius: 3, mb: 3, overflow: "auto" }}>
        <Typography variant="h6" fontWeight={700} mb={2}>Heatmap</Typography>

        {/* Grid container: ilk satır saat label'ları, sonra her gün için satır */}
        <Box sx={{ minWidth: 800 }}>
          {/* Üst saat başlığı */}
          <Box sx={{ display: "flex", gap: "2px", mb: "2px" }}>
            <Box sx={{ width: 50, flexShrink: 0 }} />
            {Array.from({ length: 24 }, (_, h) => (
              <Box
                key={h}
                sx={{
                  flex: 1, textAlign: "center", fontSize: 9, color: "#64748b",
                  fontWeight: 600
                }}
              >
                {String(h).padStart(2, "0")}
              </Box>
            ))}
          </Box>

          {/* 7 gün × 24 saat satırları */}
          {TR_DAY_LABELS.map((dayLabel, dayIdx) => (
            <Box key={dayIdx} sx={{ display: "flex", gap: "2px", mb: "2px" }}>
              <Box sx={{
                width: 50, flexShrink: 0, textAlign: "right", pr: 1,
                fontSize: 12, fontWeight: 700, color: "#475569",
                display: "flex", alignItems: "center", justifyContent: "flex-end"
              }}>
                {dayLabel}
              </Box>
              {Array.from({ length: 24 }, (_, h) => {
                const count = grid[dayIdx][h];
                const bg = cellColor(count, maxCount);
                const txt = cellTextColor(count, maxCount);
                return (
                  <Tooltip
                    key={h}
                    title={`${TR_DAY_FULL[dayIdx]} ${String(h).padStart(2, "0")}:00 → ${count} sipariş`}
                    placement="top"
                  >
                    <Box sx={{
                      flex: 1, height: 32, bgcolor: bg, color: txt,
                      borderRadius: "3px", display: "flex", alignItems: "center",
                      justifyContent: "center", fontSize: 10, fontWeight: 700,
                      cursor: "pointer", transition: "transform 0.1s",
                      "&:hover": { transform: "scale(1.15)", zIndex: 1 }
                    }}>
                      {count > 0 ? count : ""}
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          ))}

          {/* Gradient legend */}
          <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="caption" color="text.disabled" fontSize={11}>Az</Typography>
            <Box sx={{
              flex: 1, height: 12, borderRadius: 6,
              background: "linear-gradient(90deg, #e0e7ff 0%, #1e3a8a 100%)",
              maxWidth: 200
            }} />
            <Typography variant="caption" color="text.disabled" fontSize={11}>Çok ({maxCount})</Typography>
          </Box>
        </Box>
      </Card>

      {/* Yan analiz: gün bazlı + saat bazlı bar */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2.5, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={700} mb={2}>Gün Bazlı Toplam</Typography>
            {[0, 1, 2, 3, 4, 5, 6].map(trIdx => {
              // TR index → MySQL DOW
              const mysqlDow = Object.keys(MYSQL_TO_TR_INDEX).find(k => MYSQL_TO_TR_INDEX[k] === trIdx);
              const t = data.dow_totals?.[mysqlDow] || { orders: 0, revenue: 0 };
              const max = Math.max(...Object.values(data.dow_totals || {}).map(d => Number(d.orders) || 0), 1);
              const pct = (Number(t.orders) / max) * 100;
              return (
                <Box key={trIdx} sx={{ mb: 1 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography fontSize={13} fontWeight={600}>{TR_DAY_FULL[trIdx]}</Typography>
                    <Typography fontSize={13} fontWeight={700}>
                      {fmtNum(t.orders)} sipariş · {fmtMoney(t.revenue)}
                    </Typography>
                  </Box>
                  <Box sx={{ height: 6, bgcolor: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
                    <Box sx={{ height: "100%", width: `${pct}%`, bgcolor: "#1565C0", borderRadius: 3, transition: "width 0.3s" }} />
                  </Box>
                </Box>
              );
            })}
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2.5, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={700} mb={2}>Saat Bazlı Toplam</Typography>
            {/* 24 saat - 4'lü gruplar halinde göster */}
            {[
              { label: "🌙 Gece (00-06)", hours: [0, 1, 2, 3, 4, 5] },
              { label: "🌅 Sabah (06-12)", hours: [6, 7, 8, 9, 10, 11] },
              { label: "☀️ Öğle (12-18)", hours: [12, 13, 14, 15, 16, 17] },
              { label: "🌃 Akşam (18-24)", hours: [18, 19, 20, 21, 22, 23] }
            ].map(period => {
              const periodOrders = period.hours.reduce(
                (s, h) => s + (Number(data.hour_totals?.[h]?.orders) || 0), 0
              );
              const periodRevenue = period.hours.reduce(
                (s, h) => s + (Number(data.hour_totals?.[h]?.revenue) || 0), 0
              );
              const totalOrders = Object.values(data.hour_totals || {})
                .reduce((s, h) => s + (Number(h.orders) || 0), 0) || 1;
              const pct = Math.round((periodOrders / totalOrders) * 100);
              return (
                <Box key={period.label} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography fontSize={13} fontWeight={600}>{period.label}</Typography>
                    <Typography fontSize={13}>
                      <strong>{periodOrders}</strong> ({pct}%) · {fmtMoney(periodRevenue)}
                    </Typography>
                  </Box>
                  <Box sx={{ height: 6, bgcolor: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
                    <Box sx={{ height: "100%", width: `${pct}%`, bgcolor: "#10b981", borderRadius: 3, transition: "width 0.3s" }} />
                  </Box>
                </Box>
              );
            })}
          </Card>
        </Grid>
      </Grid>

      {/* Aksiyonel ipucu */}
      {data.peak && (
        <Card sx={{ p: 2, borderRadius: 3, mt: 3, bgcolor: "#fff7ed", border: "1px solid #fed7aa" }}>
          <Typography variant="subtitle2" fontWeight={700} mb={1}>
            💡 Aksiyon Önerisi
          </Typography>
          <Typography variant="body2">
            Peak saatin <strong>{peakDayTr} {peakHourLabel}</strong>. Email kampanya ve push bildirimleri bu saate <strong>30 dakika önce</strong> ({peakDayTr} {String(Math.max(0, data.peak.hour - 1)).padStart(2, "0")}:30) atılırsa müşteriler "şimdi alışveriş zamanı" hissettiği saatte mailini açar. Cumartesi/Pazar düşükse stok yenileme + admin işlerini hafta sonu yap.
          </Typography>
        </Card>
      )}
    </Box>
  );
}
